import { useState, useEffect, useCallback, useRef, ReactNode, RefObject } from 'react';
import { connect, useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import type { Dispatch } from 'redux';
import { useLocation } from 'react-router';

import { IKnm } from 'APIPackage/KnmModule/interfaces/IKnm';
import { IKnmLegalBasisParams } from 'APIPackage/KnmModule/interfaces/IKnmLegalBasisParams';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { KnmErpMenu } from 'KnmErpPackage/KnmErpMenuModule/components/KnmErpMenu';
import { LegalBasesModal } from 'KnmPackage/LegalBasesModule/components/LegalBasesModal';
import {
  knmSetFederalLawFormValue,
  knmSetKnoOrganizationErvkFormValue,
  knmSetKnoOrganizationFormValue,
  knmSetSubjectTypeFormValue,
  knmSetSupervisionTypeErvkFormValue,
  knmSetErpReasonFormValue,
  knmSetSupervisionTypeFormValue,
  knmSetTypeFormValue,
  knmSetTypeErvkFormValue,
  knmSetErpReasonErvkFormValue,
  knmSetErpKindErvkVersionIdFormValue,
} from 'KnmPackage/MainModule/actions/knmActions';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { NotMatchingFnsFieldTypesEnum } from 'KnmPackage/MainModule/enums/NotMatchingFnsFieldTypesEnum';
import { useRegistryFnsMatchData } from 'KnmPackage/MainModule/hooks/useRegistryFnsMatchData';
import { getObjectResultBlockId } from 'KnmPackage/MainModule/constants/fieldsElementsIds';
import { getKnmIsWatchOnly } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { ResultsOpenedFlagsType } from 'KnmPackage/MainModule/types/ResultsOpenedFlagsType';
import { knmRegistryGetData } from 'RegistryPackage/MainModule/actions/knmRegistryActions';
import { getKnmRegistryData } from 'RegistryPackage/MainModule/selectors/knmRegistrySelectors';
import { IKnmRegistryItem } from 'APIPackage/KnmModule/interfaces/IKnmRegistryItem';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getCoreIsLoading } from 'CorePackage/MainModule/selectors/coreSelectors';
import { requestRiskCategoriesUpdate } from 'CorePackage/DictionariesModule/actions/dictionariesActions';
import { IKnmLocationState } from 'KnmListPackage/MainModule/interfaces/IKnmLocationState';

import { checkObjectAddressMatchFns } from '../common/checkObjectAddressMatchFns';
import { IKnmErpForm } from '../interfaces/IKnmErpForm';
import { KnmErpContent } from './KnmErpContent';

interface ILegalBasesModalParams {
  legalBases?: IKnmLegalBasisParams[];
  onApply?: (existLegalBases: IKnmLegalBasisParams[], notExistLegalBasisText?: string) => void;
}

export interface IKnmErpFormRenderProps {
  knmData?: IKnm;
  formRef: IKnmFormRef;
  bodyRef: RefObject<HTMLDivElement>;
  values: IKnmErpForm;
  renderForm: (menu: ReactNode, content: ReactNode) => ReactNode;
}

interface IKnmErpFormRenderStoreProps {
  isWatchOnly: boolean;
  knmRegistryData: IKnmRegistryItem[];
  isLoading: boolean;
}

const KnmErpFormRenderWithoutStore = ({
  knmData,
  formRef,
  bodyRef,
  values,
  renderForm,
  isWatchOnly,
  knmRegistryData,
  isLoading,
}: IKnmErpFormRenderProps & IKnmErpFormRenderStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const location = useLocation<IKnmLocationState>();

  const setKnoOrganizationFormValue = useCallback(
    (knoOrganization: IKnmForm['knoOrganization']) => dispatch(knmSetKnoOrganizationFormValue(knoOrganization)),
    [dispatch],
  );
  const setKnoOrganizationErvkFormValue = useCallback(
    (knoOrganizationErvkFormValue: IKnmForm['knoOrganizationErvk']) =>
      dispatch(knmSetKnoOrganizationErvkFormValue(knoOrganizationErvkFormValue)),
    [dispatch],
  );
  const setFederalLawFormValue = useCallback(
    (federalLaw: IKnmForm['federalLaw']) => dispatch(knmSetFederalLawFormValue(federalLaw)),
    [dispatch],
  );
  const setSubjectTypeFormValue = useCallback(
    (subjectType: IKnmForm['subjectType']) => dispatch(knmSetSubjectTypeFormValue(subjectType)),
    [dispatch],
  );
  const setSupervisionTypeFormValue = useCallback(
    (supervisionType: IKnmForm['supervisionType']) => dispatch(knmSetSupervisionTypeFormValue(supervisionType)),
    [dispatch],
  );
  const setSupervisionTypeErvkFormValue = useCallback(
    (supervisionTypeErvk: IKnmForm['supervisionTypeErvk']) =>
      dispatch(knmSetSupervisionTypeErvkFormValue(supervisionTypeErvk)),
    [dispatch],
  );
  const setErpReasonFormValue = useCallback(
    (reasons: IKnmForm['reasons']) => dispatch(knmSetErpReasonFormValue(reasons)),
    [dispatch],
  );
  const setErpReasonErvkFormValue = useCallback(
    (reasons: IKnmForm['reasonsErvk']) => dispatch(knmSetErpReasonErvkFormValue(reasons)),
    [dispatch],
  );
  const setErpKindErvkVersionIdFormValue = useCallback(
    (kindErvk: IKnmForm['kindErvk']) => dispatch(knmSetErpKindErvkVersionIdFormValue(kindErvk)),
    [dispatch],
  );
  const setTypeFormValue = useCallback(
    (type: IKnmForm['type']) => {
      dispatch(knmSetTypeFormValue(type));
    },
    [dispatch],
  );

  const setTypeErvkFormValue = useCallback(
    (type: IKnmForm['typeErvk']) => {
      dispatch(knmSetTypeErvkFormValue(type));
    },
    [dispatch],
  );

  const formKnoOrganization = values.knoOrganization;
  const formKnoOrganizationErvk = values.knoOrganizationErvk;
  const formFederalLaw = values.federalLaw;
  const formSubjectType = values.subjectType;
  const formSupervisionType = values.supervisionType;
  const formSupervisionTypeErvk = values.supervisionTypeErvk;
  const formErpReasons = values.reasons;
  const formErpReasonsErvk = values.reasonsErvk;
  const formKindErvk = values.kindErvk;
  const formType = values.type;
  const formTypeErvk = values.typeErvk;

  useEffect(() => {
    setKnoOrganizationFormValue(formKnoOrganization);
  }, [setKnoOrganizationFormValue, formKnoOrganization]);

  useEffect(() => {
    setKnoOrganizationErvkFormValue(formKnoOrganizationErvk);
  }, [setKnoOrganizationErvkFormValue, formKnoOrganizationErvk]);

  useEffect(() => {
    setFederalLawFormValue(formFederalLaw);
  }, [setFederalLawFormValue, formFederalLaw]);

  useEffect(() => {
    setSubjectTypeFormValue(formSubjectType);
  }, [setSubjectTypeFormValue, formSubjectType]);

  useEffect(() => {
    setSupervisionTypeFormValue(formSupervisionType);
  }, [setSubjectTypeFormValue, formSupervisionType, setSupervisionTypeFormValue]);

  useEffect(() => {
    setSupervisionTypeErvkFormValue(formSupervisionTypeErvk);
  }, [setSupervisionTypeErvkFormValue, formSupervisionTypeErvk]);

  useEffect(() => {
    setErpReasonFormValue(formErpReasons);
  }, [setErpReasonFormValue, formErpReasons]);

  useEffect(() => {
    setErpReasonErvkFormValue(formErpReasonsErvk);
  }, [setErpReasonErvkFormValue, formErpReasonsErvk]);

  useEffect(() => {
    setErpKindErvkVersionIdFormValue(formKindErvk);
  }, [setErpKindErvkVersionIdFormValue, formKindErvk]);

  useEffect(() => {
    setTypeFormValue(formType);
  }, [setTypeFormValue, formType]);

  useEffect(() => {
    setTypeErvkFormValue(formTypeErvk);
  }, [setTypeErvkFormValue, formTypeErvk]);

  useEffect(() => {
    dispatch(requestRiskCategoriesUpdate(formKnoOrganizationErvk?.value.dictVersionId));
  }, [formKnoOrganizationErvk, formSupervisionTypeErvk]);

  useEffect(() => {
    if (location.state?.planId) {
      formRef?.current?.change('planId', String(location.state.planId));
    }
  }, [location.state?.planId]);

  useEffect(() => {
    if (knmData?.organization?.checkSubjectResult?.egrul?.id) {
      dispatch(knmRegistryGetData(knmData?.organization?.checkSubjectResult?.egrul?.id.toString(), 'egrul'));
    } else if (knmData?.organization?.checkSubjectResult?.egrip?.id) {
      dispatch(knmRegistryGetData(knmData?.organization?.checkSubjectResult?.egrip?.id.toString(), 'egrip'));
    }
  }, [knmData, dispatch]);
  const isEgrul = typeof knmData?.organization?.checkSubjectResult?.egrul?.id === 'number';
  const { registryOrganizationNameFns, registryLegalAddressFns } = useRegistryFnsMatchData({
    isLoading,
    knmRegistryData,
    isEgrul,
  });

  const formObjectsErp = values.objects;
  const formOrganizationName = values.organizationName;
  const isOrganizationNameFieldNotMatchFns =
    registryOrganizationNameFns && formOrganizationName !== registryOrganizationNameFns;
  const isObjectsLegalAddressFieldNotMatchFns =
    registryLegalAddressFns &&
    formObjectsErp?.some(formObject => checkObjectAddressMatchFns(formObject, registryLegalAddressFns));

  const [notMatchingFnsFields, setNotMatchingFnsFields] = useState<NotMatchingFnsFieldTypesEnum[]>([]);

  useEffect(() => {
    const newNotMatchingFnsFields: NotMatchingFnsFieldTypesEnum[] = [];

    if (isOrganizationNameFieldNotMatchFns) {
      newNotMatchingFnsFields.push(NotMatchingFnsFieldTypesEnum.ORGANIZATION_NAME);
    }
    if (isObjectsLegalAddressFieldNotMatchFns) {
      newNotMatchingFnsFields.push(NotMatchingFnsFieldTypesEnum.OBJECT_LEGAL_ADDRESS);
    }
    setNotMatchingFnsFields(newNotMatchingFnsFields);
  }, [isOrganizationNameFieldNotMatchFns, isObjectsLegalAddressFieldNotMatchFns]);

  const formObjects = values.objectsErvk ?? values.objects;
  const formObjectsRef = useRef<Array<{ listId: string; isNew?: boolean }> | undefined>(formObjects);

  formObjectsRef.current = formObjects;
  const formObjectsResults = values.objectsResults;
  const formObjectsResultsRef = useRef(formObjectsResults);

  formObjectsResultsRef.current = formObjectsResults;

  const [resultsOpenedFlags, setResultsOpenedFlags] = useState<ResultsOpenedFlagsType>({});

  const changeResultsOpenedFlags = useCallback(
    (id: string, value: boolean) =>
      setResultsOpenedFlags(prev => {
        if (Object.prototype.hasOwnProperty.call(prev, id)) {
          return {
            ...prev,
            [id]: value,
          };
        }

        return prev;
      }),
    [],
  );

  const formResultsIdsString = formObjectsResults
    ?.map(formObjectResult => getObjectResultBlockId(formObjectResult?.listId))
    .toString();

  useEffect(() => {
    const formResultsIds = formResultsIdsString?.split(',');

    setResultsOpenedFlags(prev => {
      const newResultsOpenedFlags: ResultsOpenedFlagsType = {};

      formResultsIds?.forEach(formResultId => {
        if (formResultId) {
          const isResultOpened = prev[formResultId];

          if (typeof isResultOpened === 'boolean') {
            newResultsOpenedFlags[formResultId] = prev[formResultId];
          } else {
            const formResult = formObjectsResultsRef.current?.find(
              item => getObjectResultBlockId(item?.listId) === formResultId,
            );

            newResultsOpenedFlags[formResultId] = !!formResult?.isNew;
          }
        }
      });

      return newResultsOpenedFlags;
    });
  }, [formResultsIdsString]);

  const [legalBasesModalParams, setLegalBasesModalParams] = useState<ILegalBasesModalParams | undefined>();

  const handleLegalBasesOpenButtonClick = useCallback(() => {
    !isWatchOnly &&
      setLegalBasesModalParams({
        legalBases: formRef.current.getFieldState('legalBases')?.value,
        onApply: (existLegalBases: IKnmLegalBasisParams[], notExistLegalBasisText?: string) => {
          formRef.current.change('legalBases', [
            ...existLegalBases,
            ...(notExistLegalBasisText?.length
              ? [
                  {
                    documentName: notExistLegalBasisText,
                    id: uuidv4(),
                  },
                ]
              : []),
          ]);
        },
      });
  }, [formRef, isWatchOnly]);

  const makeViolationsLegalBasesOpenButtonClickHandler = useCallback(
    (violationIndex: number) => () => {
      const fieldName = `violations[${violationIndex}].legalBases` as keyof IKnmForm;

      if (!isWatchOnly) {
        setLegalBasesModalParams({
          legalBases: formRef.current.getFieldState('violations')?.value?.[violationIndex].legalBases,
          onApply: (existLegalBases: IKnmLegalBasisParams[], notExistLegalBasisText?: string) => {
            formRef.current.change(fieldName, [
              ...existLegalBases,
              ...(notExistLegalBasisText?.length
                ? [
                    {
                      documentName: notExistLegalBasisText,
                      id: uuidv4(),
                    },
                  ]
                : []),
            ]);
          },
        });
      }
    },
    [formRef, isWatchOnly],
  );

  const closeLegalBasesModal = useCallback(() => setLegalBasesModalParams(undefined), []);

  const handleLegalBasesModalApply = useCallback(
    (existLegalBases: IKnmLegalBasisParams[], notExistLegalBasisText?: string) => {
      legalBasesModalParams?.onApply?.(existLegalBases, notExistLegalBasisText);
    },
    [legalBasesModalParams],
  );

  const menu = (
    <KnmErpMenu
      dataRef={bodyRef}
      notMatchingFnsFields={notMatchingFnsFields}
      changeResultsOpenedFlags={changeResultsOpenedFlags}
    />
  );

  const content = (
    <KnmErpContent
      formRef={formRef}
      knmData={knmData}
      values={values}
      isWatchOnly={isWatchOnly}
      resultsOpenedFlags={resultsOpenedFlags}
      registryOrganizationNameFns={registryOrganizationNameFns}
      registryLegalAddressFns={registryLegalAddressFns}
      notMatchingFnsFields={notMatchingFnsFields}
      onLegalBasesOpenButtonClick={handleLegalBasesOpenButtonClick}
      makeViolationsLegalBasesOpenButtonClickHandler={makeViolationsLegalBasesOpenButtonClickHandler}
      changeResultsOpenedFlags={changeResultsOpenedFlags}
    />
  );

  return (
    <>
      {renderForm(menu, content)}
      <LegalBasesModal
        visible={Boolean(legalBasesModalParams)}
        initialSelectedActs={legalBasesModalParams?.legalBases}
        onApply={handleLegalBasesModalApply}
        onClose={closeLegalBasesModal}
      />
    </>
  );
};

const mapStateToProps = (state: IRootState): IKnmErpFormRenderStoreProps => ({
  isWatchOnly: getKnmIsWatchOnly(state),
  knmRegistryData: getKnmRegistryData(state),
  isLoading: getCoreIsLoading(state),
});

export const KnmErpFormRender = connect(mapStateToProps)(KnmErpFormRenderWithoutStore);
